import { IconButton } from '@creditornot/cb-button';
import { Menu } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors, WoltLogo } from '@creditornot/cb-ingredients';
import React from 'react';
import { useParams } from 'react-router';
import { matchPath, NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { useThemeMode } from 'common/themeMode';
import { FeatureType } from 'common/types';
// TODO: @navjotahuja92 - Remove this import. Once old experience is removed.
import { Footer } from 'modules/experience/components/layout/Footer';
import UserDropdown from 'modules/header/components/UserDropdown';
import SearchItemSelector from 'modules/search-item-selector/SearchItemSelector';

import CollapsibleSidebarItem from './CollapsibleSidebarItem';
import { AvailableRouteItem, TooltipItem } from './types';

type CollapsibleSidebarProps = {
  items: AvailableRouteItem[];
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
  activeTabId?: string;
  feature: FeatureType;
  tooltip?: TooltipItem;
};

const CollapsibleSidebarContainer = styled.div<{ isCollapsed: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 32px 10px;
  min-height: 100vh;
  width: ${({ isCollapsed }) => (isCollapsed ? '68px' : '260px')};

  @media (max-width: ${MOBILE_BREAKPOINT + 1}px) {
    width: 100%;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-block-start: 18px;
  padding: 6px;
  gap: 8px;
  @media (max-height: ${MOBILE_BREAKPOINT + 1}px) {
    position: relative;
    bottom: 0;
  }
`;

const CollapsibleSidebarHeader = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  color: ${cbSemanticCssVars.textSecondary};
  fill: ${cbSemanticCssVars.textSecondary};
  width: 100%;
  justify-content: ${({ isCollapsed }) => (isCollapsed ? 'center' : 'flex-start')};
  margin-block-end: 20px;
  padding-inline-start: ${({ isCollapsed }) => (isCollapsed ? 'initial' : '6px')};
`;

const WoltSidebarLogoLinkItem = styled(NavLink)`
  position: relative;
  padding: 8px 16px;
  min-width: auto;
  top: 0;
  height: 36px;
`;

const WoltSidebarLogo = styled(WoltLogo)`
  height: 22px;
  padding: 2px 0;
`;

const SelectorContainer = styled.div<{ isCollapsed: boolean }>`
  padding-block-end: 24px;
  position: relative;
  width: 100%;
  height: 140px;
  padding-inline-start: ${({ isCollapsed }) => (isCollapsed ? 'initial' : '6px')};
`;

const SidebarItemContainer = styled.div<{ isCollapsed: boolean }>`
  margin-block-end: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-inline-start: ${({ isCollapsed }) => (isCollapsed ? 'initial' : '2px')};
`;

const TopContainer = styled.div<{ isCollapsed: Boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${({ isCollapsed }) => (isCollapsed ? 'center' : 'initial')};
`;

const StyledFooter = styled(Footer)`
  padding: 6px;
`;

type Params = {
  feature?: string;
  slug?: string;
};

const CollapsibleSidebar = ({ items, isCollapsed, setIsCollapsed, feature, tooltip }: CollapsibleSidebarProps) => {
  const { themeMode } = useThemeMode();
  const params = useParams<Params>();
  const { slug } = params;
  const logoLinkTo = feature === 'identity-verification' ? '/identity-verification' : '';
  const isMobile = useIsMobile();

  const activeTabId = items.find(route => matchPath(location.pathname, { path: route.link, exact: route.exact }))?.link;

  return (
    <CollapsibleSidebarContainer isCollapsed={isCollapsed}>
      <TopContainer isCollapsed={isCollapsed}>
        <CollapsibleSidebarHeader isCollapsed={isCollapsed}>
          <IconButton variant="neutral" size="small" onClick={() => setIsCollapsed(!isCollapsed)}>
            <Menu />
          </IconButton>
          {isCollapsed ? null : (
            <WoltSidebarLogoLinkItem to={logoLinkTo}>
              <WoltSidebarLogo fill={themeMode === 'light' ? colors.pepper100 : colors.salt100} />
            </WoltSidebarLogoLinkItem>
          )}
        </CollapsibleSidebarHeader>

        <SelectorContainer isCollapsed={isCollapsed}>
          <SearchItemSelector selectedSlug={slug} selectedFeature={feature} isCollapsed={isCollapsed} />
        </SelectorContainer>
        <SidebarItemContainer isCollapsed={isCollapsed}>
          {items.map(item => (
            <CollapsibleSidebarItem
              item={item}
              key={item.link}
              tooltip={tooltip}
              isCollapsed={isCollapsed}
              activeTabId={activeTabId}
              setIsCollapsed={setIsCollapsed}
            />
          ))}
        </SidebarItemContainer>
      </TopContainer>

      <FooterContainer>
        <div>{!isMobile && <UserDropdown />}</div>
        {!isCollapsed && <StyledFooter />}
      </FooterContainer>
    </CollapsibleSidebarContainer>
  );
};

export default CollapsibleSidebar;
